import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Browser-based Casbin service for permission management
 * Handles scope-based authorization without requiring casbin.js library
 */
@Injectable({
  providedIn: 'root',
})
export class BrowserCasbinService {
  private userScopes$ = new BehaviorSubject<any[]>([]);
  private userId$ = new BehaviorSubject<string | null>(null);
  private organizationId$ = new BehaviorSubject<string | null>(null);
  private initialized$ = new BehaviorSubject<boolean>(false);

  constructor() {}

  /**
   * Initialize Casbin with user scopes from OAuth
   */
  async initialize(
    userId: string,
    organizationId: string,
    scopes: any[],
  ): Promise<void> {
    try {

      this.userId$.next(userId);
      this.organizationId$.next(organizationId);
      this.userScopes$.next(scopes);
      this.initialized$.next(true);
    } catch (error) {
      console.error('❌ Failed to initialize Casbin:', error);
      throw error;
    }
  }

  /**
   * Check if user has specific permission based on scope format
   * Supports formats:
   * - "all" (admin access)
   * - "hb:view:sale_order" (resource:action format)
   * - "hb:update:sale_order"
   * - "hb:create:depo_order"
   */
  async hasPermission(resource: string, action: string): Promise<boolean> {
    const scopes = this.userScopes$.value;

    if (!scopes || scopes.length === 0) {
      console.warn('⚠️ No scopes available for permission check');
      return false;
    }

    // Check for admin scope (all)
    const hasAdminScope = scopes.some((scope) => scope.name === 'all');
    if (hasAdminScope) {
      return true;
    }

    // Build the expected scope name based on resource and action
    // Format: hb:{action}:{resource}
    const expectedScope = `hb:${action}:${resource}`;

    // Check if user has the specific scope
    const hasScope = scopes.some((scope) => scope.name === expectedScope);

    return hasScope;
  }

  /**
   * Check multiple permissions (OR logic - user needs at least one)
   */
  async hasAnyPermission(
    permissions: Array<{ resource: string; action: string }>,
  ): Promise<boolean> {
    for (const perm of permissions) {
      if (await this.hasPermission(perm.resource, perm.action)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Check multiple permissions (AND logic - user needs all)
   */
  async hasAllPermissions(
    permissions: Array<{ resource: string; action: string }>,
  ): Promise<boolean> {
    for (const perm of permissions) {
      if (!(await this.hasPermission(perm.resource, perm.action))) {
        return false;
      }
    }
    return true;
  }

  /**
   * Check if user has a specific scope by name
   */
  hasScope(scopeName: string): boolean {
    const scopes = this.userScopes$.value;
    return scopes.some((scope) => scope.name === scopeName);
  }

  /**
   * Check if user has any scope matching a pattern
   */
  hasScopePattern(pattern: RegExp | string): boolean {
    const scopes = this.userScopes$.value;
    const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;
    return scopes.some((scope) => regex.test(scope.name));
  }

  /**
   * Get all user scopes
   */
  getUserScopes(): any[] {
    return this.userScopes$.value;
  }

  /**
   * Get user scopes as observable
   */
  getUserScopes$(): Observable<any[]> {
    return this.userScopes$.asObservable();
  }

  /**
   * Get current user ID
   */
  getCurrentUserId(): string | null {
    return this.userId$.value;
  }

  /**
   * Get current user ID as observable
   */
  getCurrentUser$(): Observable<string | null> {
    return this.userId$.asObservable();
  }

  /**
   * Get current organization ID
   */
  getOrganizationId(): string | null {
    return this.organizationId$.value;
  }

  /**
   * Check if Casbin is initialized
   */
  isInitialized(): boolean {
    return this.initialized$.value;
  }

  /**
   * Get initialization status as observable
   */
  isInitialized$(): Observable<boolean> {
    return this.initialized$.asObservable();
  }

  /**
   * Clear all permissions (use on logout)
   */
  clear(): void {
    this.userScopes$.next([]);
    this.userId$.next(null);
    this.organizationId$.next(null);
    this.initialized$.next(false);
  }

  /**
   * Refresh user scopes
   */
  async refreshScopes(scopes: any[]): Promise<void> {
    this.userScopes$.next(scopes);
  }

  /**
   * Get scopes by action (view, create, update, delete)
   */
  getScopesByAction(action: string): any[] {
    const scopes = this.userScopes$.value;
    return scopes.filter((scope) => scope.name.includes(`:${action}:`));
  }

  /**
   * Get scopes by resource
   */
  getScopesByResource(resource: string): any[] {
    const scopes = this.userScopes$.value;
    return scopes.filter((scope) => scope.name.endsWith(`:${resource}`));
  }

  /**
   * Check if user is admin (has 'all' scope)
   */
  isAdmin(): boolean {
    return this.hasScope('all');
  }

  /**
   * Get all unique resources user has access to
   */
  getAccessibleResources(): string[] {
    const scopes = this.userScopes$.value;
    const resources = new Set<string>();

    scopes.forEach((scope) => {
      if (scope.name !== 'all' && scope.name.includes(':')) {
        const parts = scope.name.split(':');
        if (parts.length === 3) {
          resources.add(parts[2]); // Extract resource name
        }
      }
    });

    return Array.from(resources);
  }

  /**
   * Get all unique actions user can perform
   */
  getAccessibleActions(): string[] {
    const scopes = this.userScopes$.value;
    const actions = new Set<string>();

    scopes.forEach((scope) => {
      if (scope.name !== 'all' && scope.name.includes(':')) {
        const parts = scope.name.split(':');
        if (parts.length === 3) {
          actions.add(parts[1]); // Extract action
        }
      }
    });

    return Array.from(actions);
  }

  /**
   * Debug: Log all user permissions
   */
  logUserPermissions(): void {
    console.table(this.userScopes$.value);
    console.groupEnd();
  }
}
